import { Alert, AlertVariants, Button, FormError, ModalDialog } from '@ctek/design-system';
import { useDataLoader } from 'century-core/core-utils/hooks/use-data-loader';
import { TeacherDashboardContext } from 'century-core/features-dashboard/TeacherDashboard/common/TeacherDashboardContext';
import { Settings } from 'century-core/features-dashboard/TeacherDashboard/TeacherDashboardAssignmentsV3/components/Settings';
import { validateAssignment } from 'century-core/features-dashboard/TeacherDashboard/TeacherDashboardAssignmentsV3/helpers/validateAssignment';
import { Form, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { useContext, useState } from 'react';
import { MixpanelKeys } from 'century-core/core-utils/utils/mixpanel/MixpanelKeys';
import { MixpanelEventTypes } from 'century-core/core-utils/utils/mixpanel/MixpanelEventTypes';
import { useHistory } from 'react-router-dom';
import { useAbortController } from 'century-core/core-utils/hooks/use-abort-controller';
import { useAccessTokenRef } from 'century-core/core-auth/hooks/useAuth';
import { buildAssignmentPayload } from 'century-core/features-dashboard/TeacherDashboard/TeacherDashboardAssignmentsV3/helpers/createOrEditAssignment';
import { addStudentGroup, createNewAssignment } from 'century-core/core-apis/assignment/assignments';
import {
  AssignmentFormData,
  createInitialSettings,
  getAllUniqueSubjects,
} from 'century-core/features-dashboard/TeacherDashboard/TeacherDashboardAssignmentsV3/helpers/helpers';
import mixpanel from 'mixpanel-browser';

type CreateStudentAssignmentModalProps = {
  studentName: string;
  studentId: string;
  closeModal: () => void;
};
export const CreateStudentAssignmentModal = (props: CreateStudentAssignmentModalProps) => {
  const { studentName, studentId, closeModal } = props;
  const [responseError, setResponseError] = useState('');
  const intl = useIntl();
  const tokenRef = useAccessTokenRef();
  const history = useHistory();
  const { getSignal: createAssignmentSignal } = useAbortController();
  const { getSignal: addStudentGroupSignal } = useAbortController();
  const trackStudentAssignmentCreation = useStudentAssignmentModalMixpanel(studentId);

  const { baseData } = useContext(TeacherDashboardContext);
  const getAllSubjects = async (token: string, signal: AbortSignal) => await getAllUniqueSubjects(token, baseData?.courses, intl, signal);
  const { data: combinedSubjects, error: subjectsError } = useDataLoader(getAllSubjects, true);

  const submitForm = async (values: AssignmentFormData) => {
    try {
      const newAssignment = buildAssignmentPayload(values);
      const { assignmentId } = await createNewAssignment(tokenRef.current, newAssignment, createAssignmentSignal());
      await addStudentGroup(tokenRef.current, assignmentId, [studentId], addStudentGroupSignal());
      trackStudentAssignmentCreation('submitted');

      closeModal();
      history.push(`/teach/assignments/${assignmentId}?csat=assignments`);
    } catch (err) {
      setResponseError(err.message);
    }
  };

  const dismissModal = () => {
    trackStudentAssignmentCreation('dismissed');
    closeModal();
  };

  const titleLabel = intl.formatMessage(
    { id: 'create-student-assignment-modal-title', defaultMessage: 'Create Assignment for {studentName}' },
    { studentName }
  );
  const dismissLabel = intl.formatMessage({ id: 'modal-dismiss-button', defaultMessage: 'Dismiss' });

  return (
    <Formik<AssignmentFormData>
      initialValues={createInitialSettings(combinedSubjects ? combinedSubjects.flatMap(group => group.entities)[0].value : '')}
      validateOnBlur={false}
      validateOnChange={false}
      validate={validateAssignment}
      onSubmit={submitForm}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <ModalDialog qa="one-student-assignment-modal" modalActive={true} onExit={dismissModal} dismissLabel={dismissLabel}>
            <ModalDialog.Header>
              <ModalDialog.Title>{titleLabel}</ModalDialog.Title>
            </ModalDialog.Header>
            {subjectsError || !combinedSubjects?.length ? (
              <Alert variant={AlertVariants.WARNING}>
                <FormattedMessage id="assign-modal-data-error" defaultMessage="There was a problem loading this data." />
              </Alert>
            ) : (
              <ModalDialog.Body>
                <Form onSubmit={handleSubmit} autoComplete="off">
                  <Settings subjects={combinedSubjects} />
                  {responseError && (
                    <div style={{ marginTop: 18 }}>
                      <FormError qa={'response-error'}>{responseError}</FormError>
                    </div>
                  )}
                </Form>
              </ModalDialog.Body>
            )}
            <ModalDialog.Controls>
              <Button size="medium" type="button" variant="default" qa="cancel-button" onClick={dismissModal}>
                <FormattedMessage id="cancel-btn" defaultMessage="cancel" />
              </Button>
              <Button
                size="medium"
                type="submit"
                variant="primary"
                qa="create-button"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                <FormattedMessage id="create-btn" defaultMessage="Create" />
              </Button>
            </ModalDialog.Controls>
          </ModalDialog>
        );
      }}
    </Formik>
  );
};

const useStudentAssignmentModalMixpanel = (studentId: string) => {
  return (action: 'dismissed' | 'submitted') => {
    mixpanel.track(MixpanelEventTypes.TeacherDashboardStudentAssignmentsCreateModal, {
      [MixpanelKeys.ModalAction]: action,
      [MixpanelKeys.StudentId]: studentId,
    });
  };
};
